import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Heart, Instagram, Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { INSTAGRAM_URL } from '../content';

const DonateModal = ({ open, onClose }) => {
    const { t } = useLanguage();
    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[120] flex items-center justify-center px-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    data-testid="donate-modal"
                >
                    <div className="absolute inset-0 bg-ink/90 backdrop-blur-sm" onClick={onClose} />
                    <motion.div
                        className="relative bg-surface border border-white/10 max-w-md w-full p-10 md:p-12"
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 20, opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <Heart className="w-6 h-6 text-dusk mb-6" strokeWidth={1.5} />
                        <h3 className="font-serif text-3xl text-paper tracking-tight mb-4" data-testid="donate-modal-title">{t.donate.title}</h3>
                        <p className="text-fog text-sm leading-relaxed mb-8">{t.donate.body}</p>
                        <div className="flex flex-wrap items-center gap-4">
                            <a
                                href={INSTAGRAM_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 rounded-full bg-dusk px-6 py-3 text-sm font-medium text-ink transition-colors duration-300 hover:bg-dusk-hover"
                                data-testid="donate-instagram-button"
                            >
                                <Instagram className="w-4 h-4" /> {t.donate.follow}
                            </a>
                            <button
                                onClick={onClose}
                                className="text-sm text-fog hover:text-paper transition-colors duration-300 link-line"
                                data-testid="donate-close-button"
                            >
                                {t.donate.close}
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export const useDonate = () => {
    const [open, setOpen] = useState(false);
    return { open, setOpen };
};

const Nav = ({ onDonate }) => {
    const { lang, setLang, t } = useLanguage();
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    const links = [
        { to: '/', label: t.nav.home },
        { to: '/gallery', label: t.nav.gallery },
        { to: '/about', label: t.nav.about },
        { to: '/cv', label: t.nav.cv },
        { to: '/shop', label: t.nav.shop },
        { to: '/contact', label: t.nav.contact },
    ];

    return (
        <>
            <motion.header
                className={`fixed top-0 left-0 w-full z-[100] transition-colors duration-500 ${
                    scrolled || menuOpen ? 'bg-black/60 backdrop-blur-xl border-b border-white/10' : 'border-b border-transparent'
                }`}
                initial={{ y: -80 }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                data-testid="main-nav"
            >
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between gap-6">
                    <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo-link">
                        <img
                            src="/images/art-01.jpg"
                            alt="Robin Spencer signature"
                            className="h-9 w-auto invert opacity-90 transition-opacity duration-300 group-hover:opacity-100"
                        />
                        <span className="font-serif text-xl tracking-tight text-paper hidden sm:block">Robin Spencer</span>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === '/'}
                                className={({ isActive }) =>
                                    `link-line text-sm tracking-wide transition-colors duration-300 ${
                                        isActive ? 'active text-paper' : 'text-fog hover:text-paper'
                                    }`
                                }
                                data-testid={`nav-link-${l.to === '/' ? 'home' : l.to.slice(1)}`}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-4 md:gap-6">
                        <div className="flex items-center text-xs tracking-[0.2em] font-medium" data-testid="language-toggle">
                            <button
                                onClick={() => setLang('fr')}
                                className={`px-1.5 py-1 transition-colors duration-300 ${lang === 'fr' ? 'text-dusk' : 'text-fog hover:text-paper'}`}
                                data-testid="lang-fr-button"
                                aria-pressed={lang === 'fr'}
                            >
                                FR
                            </button>
                            <span className="text-white/20">/</span>
                            <button
                                onClick={() => setLang('en')}
                                className={`px-1.5 py-1 transition-colors duration-300 ${lang === 'en' ? 'text-dusk' : 'text-fog hover:text-paper'}`}
                                data-testid="lang-en-button"
                                aria-pressed={lang === 'en'}
                            >
                                EN
                            </button>
                        </div>

                        <button
                            onClick={onDonate}
                            className="hidden sm:inline-flex items-center gap-2 rounded-full border border-dusk/50 px-5 py-2 text-sm text-dusk transition-colors duration-300 hover:bg-dusk hover:text-ink"
                            data-testid="donate-button"
                        >
                            <Heart className="w-3.5 h-3.5" /> {t.nav.donate}
                        </button>

                        <button
                            onClick={() => setMenuOpen(!menuOpen)}
                            className="lg:hidden text-paper p-2"
                            aria-label={menuOpen ? t.nav.close : t.nav.menu}
                            data-testid="mobile-menu-button"
                        >
                            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>
            </motion.header>

            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        className="fixed inset-0 z-[95] bg-ink flex flex-col justify-center px-8"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        data-testid="mobile-menu"
                    >
                        <nav className="flex flex-col gap-2" aria-label="Mobile">
                            {links.map((l, i) => (
                                <span key={l.to} className="block overflow-hidden">
                                    <motion.span
                                        className="block"
                                        initial={{ y: '110%' }}
                                        animate={{ y: 0 }}
                                        transition={{ duration: 0.7, delay: 0.1 + i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                                    >
                                        <NavLink
                                            to={l.to}
                                            end={l.to === '/'}
                                            className={({ isActive }) =>
                                                `font-serif text-5xl tracking-tight ${isActive ? 'text-dusk' : 'text-paper'}`
                                            }
                                            data-testid={`mobile-nav-link-${l.to === '/' ? 'home' : l.to.slice(1)}`}
                                        >
                                            {l.label}
                                        </NavLink>
                                    </motion.span>
                                </span>
                            ))}
                        </nav>
                        <motion.button
                            onClick={() => { setMenuOpen(false); onDonate(); }}
                            className="mt-12 inline-flex w-fit items-center gap-2 rounded-full border border-dusk/50 px-6 py-3 text-dusk"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            data-testid="mobile-donate-button"
                        >
                            <Heart className="w-4 h-4" /> {t.nav.donate}
                        </motion.button>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export { DonateModal };
export default Nav;
